/**
 * @fileoverview Colors Section - Component Library
 *
 * Purpose: Display all color swatches and palettes from the design system
 * Used by: Component library main page
 */

import { ColorSwatch } from "./-ui.color-swatch";
import { ColorSection } from "./-ui.color-section";

export function ColorsSection() {
  return (
    <div id="colors" className="scroll-mt-24 space-y-12">
      <div>
        <h1 className="mb-2 text-4xl font-bold tracking-tight">Colors</h1>
        <p className="text-lg text-muted-foreground">
          Our design system uses a semantic color palette powered by CSS
          variables. Colors automatically adapt between light and dark themes.
        </p>
      </div>

      {/* Primary Colors */}
      <div id="primary-colors" className="scroll-mt-24">
        <ColorSection
          title="Primary Colors"
          description="Main brand colors used for primary actions, links, and key UI elements."
        >
          <ColorSwatch
            name="Primary"
            variable="--primary"
            lightValue="240 5.9% 10%"
            darkValue="0 0% 98%"
            className="bg-primary"
          />
          <ColorSwatch
            name="Primary Foreground"
            variable="--primary-foreground"
            lightValue="0 0% 98%"
            darkValue="240 5.9% 10%"
            className="bg-primary-foreground"
          />
          <ColorSwatch
            name="Secondary"
            variable="--secondary"
            lightValue="0 0% 98%"
            darkValue="240 3.7% 15.9%"
            className="bg-secondary"
          />
          <ColorSwatch
            name="Secondary Foreground"
            variable="--secondary-foreground"
            lightValue="240 5.9% 10%"
            darkValue="0 0% 98%"
            className="bg-secondary-foreground"
          />
        </ColorSection>
      </div>

      {/* Semantic Colors */}
      <div id="semantic-colors" className="scroll-mt-24">
        <ColorSection
          title="Semantic Colors"
          description="Colors that communicate meaning and state (destructive actions, muted content, accents)."
        >
          <ColorSwatch
            name="Destructive"
            variable="--destructive"
            lightValue="0 84.2% 60.2%"
            darkValue="0 62.8% 30.6%"
            className="bg-destructive"
          />
          <ColorSwatch
            name="Destructive Foreground"
            variable="--destructive-foreground"
            lightValue="0 0% 98%"
            darkValue="0 0% 98%"
            className="bg-destructive-foreground"
          />
          <ColorSwatch
            name="Muted"
            variable="--muted"
            lightValue="240 4.8% 95.9%"
            darkValue="240 3.7% 15.9%"
            className="bg-muted"
          />
          <ColorSwatch
            name="Muted Foreground"
            variable="--muted-foreground"
            lightValue="240 3.8% 46.1%"
            darkValue="240 5% 64.9%"
            className="bg-muted-foreground"
          />
          <ColorSwatch
            name="Accent"
            variable="--accent"
            lightValue="38 81% 81%"
            darkValue="37 40% 53%"
            className="bg-accent"
          />
          <ColorSwatch
            name="Accent Foreground"
            variable="--accent-foreground"
            lightValue="240 5.9% 10%"
            darkValue="0 0% 98%"
            className="bg-accent-foreground"
          />
        </ColorSection>
      </div>

      {/* Surface Colors */}
      <div id="surface-colors" className="scroll-mt-24">
        <ColorSection
          title="Surface Colors"
          description="Background colors for different surface types (page background, cards, popovers)."
        >
          <ColorSwatch
            name="Background"
            variable="--background"
            lightValue="0 0% 100%"
            darkValue="0 0% 0%"
            className="border-2 bg-background"
          />
          <ColorSwatch
            name="Foreground"
            variable="--foreground"
            lightValue="240 10% 3.9%"
            darkValue="0 0% 98%"
            className="bg-foreground"
          />
          <ColorSwatch
            name="Card"
            variable="--card"
            lightValue="50 100% 99%"
            darkValue="240 10% 3.9%"
            className="border-2 bg-card"
          />
          <ColorSwatch
            name="Card Foreground"
            variable="--card-foreground"
            lightValue="240 10% 3.9%"
            darkValue="0 0% 98%"
            className="bg-card-foreground"
          />
          <ColorSwatch
            name="Popover"
            variable="--popover"
            lightValue="0 0% 100%"
            darkValue="240 10% 3.9%"
            className="border-2 bg-popover"
          />
          <ColorSwatch
            name="Popover Foreground"
            variable="--popover-foreground"
            lightValue="240 10% 3.9%"
            darkValue="0 0% 98%"
            className="bg-popover-foreground"
          />
        </ColorSection>
      </div>

      {/* Border & Input Colors */}
      <div id="border-colors" className="scroll-mt-24">
        <ColorSection
          title="Border & Input Colors"
          description="Colors used for borders, input fields, and focus rings."
        >
          <ColorSwatch
            name="Border"
            variable="--border"
            lightValue="240 5.9% 90%"
            darkValue="240 3.7% 15.9%"
            className="bg-border"
          />
          <ColorSwatch
            name="Input"
            variable="--input"
            lightValue="240 5.9% 90%"
            darkValue="240 3.7% 15.9%"
            className="bg-input"
          />
          <ColorSwatch
            name="Ring"
            variable="--ring"
            lightValue="240 5.9% 10%"
            darkValue="240 4.9% 83.9%"
            className="bg-ring"
          />
        </ColorSection>
      </div>
    </div>
  );
}
